import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Trash } from '@phosphor-icons/react';
import { API_BASE } from '../config/api';

export function ValleyOfMemories({ isOpen, onClose, onSelectSession, token }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const API = API_BASE;

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    fetch(`${API}/memory/sessions`, {
      headers: { Authorization: `Bearer ${token}` },
      credentials: 'include',
    })
      .then(r => r.json())
      .then(d => { setSessions(d.sessions || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [isOpen, token]);

  const deleteSession = async (sid, e) => {
    e.stopPropagation();
    await fetch(`${API}/memory/sessions/${sid}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
      credentials: 'include',
    });
    setSessions(prev => prev.filter(s => s.session_id !== sid));
  };

  const _seen = new Set();
  const filtered = sessions
    .filter(s => !_seen.has(s.session_id) && _seen.add(s.session_id))
    .filter(s => (s.title || '').toLowerCase().includes(search.toLowerCase()));

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 8 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-50 flex flex-col"
          style={{ background: '#030712' }}
          data-testid="valley-of-memories"
        >
          {/* Header */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 20px',
            borderBottom: '1px solid rgba(59,130,246,0.12)',
            flexShrink: 0,
          }}>
            <button onClick={onClose} style={{
              display: 'flex', alignItems: 'center', gap: 8,
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'rgba(255,255,255,0.35)', fontSize: 12,
            }}
              onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.65)'}
              onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.35)'}
              data-testid="valley-back">
              <ArrowLeft size={16} />
              <span style={{ fontFamily: 'monospace', letterSpacing: '.1em', textTransform: 'uppercase', fontSize: 11 }}>Voltar</span>
            </button>
            <h2 style={{ fontSize: 14, fontWeight: 500, color: '#3B82F6', letterSpacing: '.04em', margin: 0 }}>
              Histórico
            </h2>
            <div style={{ width: 80 }} />
          </div>

          {/* Search */}
          <div style={{ padding: '12px 20px 8px', flexShrink: 0 }}>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Buscar conversas..."
              style={{
                width: '100%',
                padding: '8px 14px',
                background: '#07111F',
                border: '1px solid #1a2240',
                borderRadius: 10,
                color: '#e8eaf2',
                fontSize: 12,
                outline: 'none',
                boxSizing: 'border-box',
              }}
              onFocus={e => e.target.style.borderColor = '#3B82F6'}
              onBlur={e => e.target.style.borderColor = '#1a2240'}
            />
          </div>

          {/* List */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '4px 20px 20px' }}>
            {loading ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200 }}>
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1.8, repeat: Infinity, ease: 'linear' }}
                  style={{ width: 22, height: 22, borderRadius: '50%', border: '2px solid #1a2240', borderTopColor: '#3B82F6' }}
                />
              </div>
            ) : filtered.length === 0 ? (
              <div style={{ textAlign: 'center', paddingTop: 60, color: 'rgba(255,255,255,0.15)', fontSize: 13 }}>
                {search ? 'Nenhuma conversa encontrada' : 'Nenhuma conversa ainda'}
              </div>
            ) : (
              filtered.map((s, i) => (
                <motion.div
                  key={s.session_id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(i * 0.04, 0.3) }}
                  onClick={() => { onSelectSession(s.session_id); onClose(); }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => { if (e.key === 'Enter') { onSelectSession(s.session_id); onClose(); } }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '12px 14px',
                    borderRadius: 12,
                    border: '1px solid rgba(255,255,255,0.05)',
                    background: 'rgba(255,255,255,0.02)',
                    marginBottom: 6,
                    cursor: 'pointer',
                    transition: 'all 0.18s',
                    outline: 'none',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = 'rgba(59,130,246,0.04)';
                    e.currentTarget.style.borderColor = 'rgba(59,130,246,0.15)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.02)';
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.05)';
                  }}
                  data-testid={`memory-node-${i}`}
                >
                  {/* Indicator dot */}
                  <div style={{
                    width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
                    background: i === 0 ? '#3B82F6' : 'rgba(255,255,255,0.12)',
                  }} />

                  {/* Content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{
                      margin: 0,
                      fontSize: 13,
                      color: 'rgba(255,255,255,0.75)',
                      fontWeight: 400,
                      lineHeight: 1.45,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}>
                      {s.title || 'Conversa sem título'}
                    </p>
                    <div style={{ display: 'flex', gap: 10, marginTop: 3 }}>
                      <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.2)', fontFamily: 'monospace' }}>
                        {new Date(s.created_at).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: '2-digit' })}
                      </span>
                      <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.15)' }}>
                        {s.message_count || 0} msgs
                      </span>
                    </div>
                  </div>

                  {/* Delete */}
                  <button
                    onClick={e => deleteSession(s.session_id, e)}
                    style={{
                      flexShrink: 0,
                      width: 28, height: 28,
                      borderRadius: 7,
                      background: 'none',
                      border: '1px solid transparent',
                      cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: 'rgba(255,255,255,0.2)',
                      transition: 'all 0.15s',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.background = 'rgba(239,68,68,0.08)';
                      e.currentTarget.style.borderColor = 'rgba(239,68,68,0.2)';
                      e.currentTarget.style.color = '#ef4444';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.background = 'none';
                      e.currentTarget.style.borderColor = 'transparent';
                      e.currentTarget.style.color = 'rgba(255,255,255,0.2)';
                    }}
                  >
                    <Trash size={12} />
                  </button>
                </motion.div>
              ))
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
